import { QuestionTypes } from '@showbie-socrative/socrative-utils';

import { translate } from '~l10n/client';
import { calculateStudentQuizTotal } from '~teacher/components/ActivityResult/scoreCalculation';
import {
  QuestionScore,
  Student,
  StudentActivityResponse,
} from '~teacher/models/activities';
import { QuizAnswer } from '~teacher/models/quizzes';

import { answerNumberToLetter } from '../../../../util/answerNumberToLetter';
import { NormalizedQuestion } from './normalizeQuestions';
import { NormalizedResponse } from './normalizeResponses';
import { NormalizedStudent } from './normalizeStudent';

function responseToQuestion({
  studentResponseToQuestion,
  answers,
}: {
  studentResponseToQuestion: StudentActivityResponse;
  answers: Record<string, QuizAnswer>;
}): {
  id: number;
  questionId: number;
  userUuid: string;
  isCorrect: boolean;
  answerText: any;
  isAnswered?: boolean;
  hidden: boolean;
} {
  switch (studentResponseToQuestion.type) {
    case QuestionTypes.TrueFalse:
      return {
        id: studentResponseToQuestion.id,
        questionId: studentResponseToQuestion.questionId,
        userUuid: studentResponseToQuestion.userUuid,
        isCorrect: studentResponseToQuestion.isCorrect,
        answerText: translate(
          answers[studentResponseToQuestion.selectedAnswer]?.text
        ),
        hidden: studentResponseToQuestion.hidden,
      };
    case QuestionTypes.MultipleChoice: {
      const answerText = studentResponseToQuestion.selectedAnswers
        .map((sa) => answers[sa]?.order)
        .sort((a, b) => a - b)
        .map(answerNumberToLetter)
        .join(', ');

      return {
        id: studentResponseToQuestion.id,
        questionId: studentResponseToQuestion.questionId,
        userUuid: studentResponseToQuestion.userUuid,
        isCorrect: studentResponseToQuestion.isCorrect,
        answerText,
        hidden: studentResponseToQuestion.hidden,
      };
    }
    case QuestionTypes.FreeResponse:
      return {
        id: studentResponseToQuestion.id,
        questionId: studentResponseToQuestion.questionId,
        userUuid: studentResponseToQuestion.userUuid,
        isCorrect: studentResponseToQuestion.isCorrect,
        answerText: studentResponseToQuestion.textResponse,
        isAnswered: true,
        hidden: studentResponseToQuestion.hidden,
      };
  }
}

export const populateParticipants = ({
  students,
  responses,
  questions,
  questionScores,
  answers,
}: {
  students: NormalizedStudent;
  responses: NormalizedResponse;
  questions: NormalizedQuestion;
  answers: Record<string, QuizAnswer>;
  questionScores: Record<string, QuestionScore>;
}) => {
  return Object.values(students.entities).map((student: Student) => {
    let numQuestionsAnswered = 0;
    const responsesByQuestionId = {};

    questions.byOrder.forEach((questionId) => {
      responsesByQuestionId[questionId] = [];
      const questionResponses = new Set(responses.byQuestionId[questionId]);

      const studentResponsesToQuestion: Array<number> = [
        ...(responses.byStudentId[student.id] ?? []),
      ].filter((response) => questionResponses.has(response));

      const questionScore = questionScores ? questionScores[questionId] : null;
      const studentScore = questionScore
        ? questionScore.studentScores[student.id]
        : null;

      if (studentResponsesToQuestion.length > 0 || studentScore) {
        // This should ensure only latest response is used (for multiple attempts or duplicated live events)

        numQuestionsAnswered++;

        studentResponsesToQuestion.forEach((responseId) => {
          const response = responses.entities[responseId];
          if (response) {
            responsesByQuestionId[response.questionId].push(
              responseToQuestion({
                studentResponseToQuestion: response,
                answers,
              })
            );
          }
        });
      }
    });

    const rawScore = calculateStudentQuizTotal(
      student,
      Object.values(questions.entities),
      responses,
      questionScores
    );

    return {
      id: student.id,
      uuid: student.userUuid,
      here: student.isPresent,
      finished: student.isFinished,
      name: student.name,
      responsesByQuestion: responsesByQuestionId,
      currentGrade: rawScore,
      questionsAnswered: numQuestionsAnswered,
    };
  });
};
